import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Avatar from '../components/Avatar';
import Section from '../components/Section';
import WeekTabs from '../components/WeekTabs';
import HeaderBar from '../components/HeaderBar';
import type { DiaryItem } from '../models/diary';
import { MealType } from '../models/diary';
import DiaryItemCard from '../components/DiaryItemCard';
import AdherenceChart from '../components/AdherenceChart';
import { adherenceService, AdherenceStatistic } from '../services/adherenceService';
import { weeklyRecordsService, WeeklyRecord } from '../services/weeklyRecordsService';
import WellnessBarChart from '../components/WellnessBarChart';
import Tabs from '../components/Tabs';
import PatientDataForm, { PatientData } from '../components/PatientDataForm';
import DiaryItemEditModal from '../components/DiaryItemEditModal';
import DiaryItemCreateModal from '../components/DiaryItemCreateModal';
import DocumentManager from '../components/DocumentManager';
import { patientsService, Patient } from '../services/patientsService';
import { diaryService } from '../services/diaryService';

const weekItems = [
  ['Desayuno', 'Ejercicio', 'Almuerzo'],
  ['Entrenamiento', 'Comida 1', 'Comida 2', 'Snack'],
  ['Yoga', 'Comida 1', 'Comida 2'],
  ['Descanso', 'Comida 1', 'Comida 2', 'Snack'],
  ['Ejercicio', 'Comida 1', 'Comida 2'],
  ['Caminata', 'Comida 1', 'Comida 2', 'Snack'],
  ['Libre', 'Comida 1', 'Comida 2'],
];

// Fallback data for form compatibility
const fallbackPatientData = {
  email: '',
  username: '',
  firstName: '',
  lastName: '',
  avatar: null,
  phoneNumber: '',
  gender: 'male' as 'male',
  address: '',
  dateOfBirth: '',
  rmrKcalDay: 0,
  weight: 0,
};



const PatientDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedDay, setSelectedDay] = useState(1); // Default to Martes
  const [editMode, setEditMode] = useState(false);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0); // 0: Statistics, 1: Plan, 2: Other
  const [planItems, setPlanItems] = useState<DiaryItem[]>([]);
  const [diaryLoading, setDiaryLoading] = useState(false);
  const [diaryError, setDiaryError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<DiaryItem | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [adherenceData, setAdherenceData] = useState<AdherenceStatistic[]>([]);
  const [adherenceLoading, setAdherenceLoading] = useState(false);
  const [adherenceError, setAdherenceError] = useState<string | null>(null);
  const [weeklyRecordsData, setWeeklyRecordsData] = useState<WeeklyRecord[]>([]);
  const [weeklyRecordsLoading, setWeeklyRecordsLoading] = useState(false);
  const [weeklyRecordsError, setWeeklyRecordsError] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<DiaryItem | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const items = weekItems[selectedDay];
  
  // Map selectedDay to day names for filtering diary items
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday','Sunday'];
  const selectedDayName = dayNames[selectedDay];
  
  // Filter diary items by selected day and sort by order
  const filteredPlanItems = planItems
    .filter(item => 
      item.dayOfWeek === selectedDayName || 
      (item.dayOfWeek && item.dayOfWeek.toLowerCase() === selectedDayName.toLowerCase())
    )
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Fetch patient data when component mounts or ID changes
  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) {
        setError('No patient ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const patientData = await patientsService.getPatientById(id);
        setPatient(patientData);
      } catch (err) {
        setError('Failed to load patient data');
        console.error('Error fetching patient:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  // Fetch diary items when patient ID changes
  useEffect(() => {
    const fetchDiaryItems = async () => {
      if (!id) return;

      try {
        setDiaryLoading(true);
        setDiaryError(null);
        const diaryItems = await diaryService.getAllDiaryItemsByUserId(id);
        console.log('Fetched diary items:', diaryItems);
        // Sort items by order field when initially loading
        const sortedItems = diaryItems.sort((a, b) => (a.order || 0) - (b.order || 0));
        setPlanItems(sortedItems);
      } catch (err) {
        setDiaryError('Failed to load diary items');
        console.error('Error fetching diary items:', err);
        setPlanItems([]);
      } finally {
        setDiaryLoading(false);
      }
    };

    fetchDiaryItems();
  }, [id]);

  // Fetch adherence data when patient data is loaded
  useEffect(() => {
    const fetchAdherenceData = async () => {
      if (!patient?.userId) return;

      try {
        setAdherenceLoading(true);
        setAdherenceError(null);
        const adherenceStats = await adherenceService.getLastTenWeeksAdherence(patient.userId);
        console.log('Fetched adherence data:', adherenceStats);
        setAdherenceData(adherenceStats);
      } catch (err) {
        setAdherenceError('Failed to load adherence data');
        console.error('Error fetching adherence data:', err);
        setAdherenceData([]);
      } finally {
        setAdherenceLoading(false);
      }
    };

    fetchAdherenceData();
  }, [patient?.userId]);

  // Fetch weekly records data when patient data is loaded
  useEffect(() => {
    const fetchWeeklyRecordsData = async () => {
      if (!patient?.id) return;

      try {
        setWeeklyRecordsLoading(true);
        setWeeklyRecordsError(null);
        const weeklyRecords = await weeklyRecordsService.getLastTenWeeksRecords(patient.userId);
        console.log('Fetched weekly records data:', weeklyRecords);
        setWeeklyRecordsData(weeklyRecords);
      } catch (err) {
        setWeeklyRecordsError('Failed to load weekly records data');
        console.error('Error fetching weekly records data:', err);
        setWeeklyRecordsData([]);
      } finally {
        setWeeklyRecordsLoading(false);
      }
    };

    fetchWeeklyRecordsData();
  }, [patient?.id]);

  // Open modal for editing
  const handleEditItem = (item: DiaryItem) => {
    setEditingItem({ ...item });
    setEditModalOpen(true);
  };
  // Handle field change in modal
  const handleEditField = (field: string, value: any) => {
    if (!editingItem) return;
    setEditingItem({ ...editingItem, [field]: value });
  };
  // Save changes
  const handleSaveEdit = async () => {
    if (!editingItem || !id) return;
    
    try {
      // Call the API to update the diary item
      const updatedItem = await diaryService.updateDiaryItem(id, editingItem.id, editingItem);
      
      // Update local state with the response from the API and sort by order
      setPlanItems(items => {
        const updatedItems = items.map(i => i.id === editingItem.id ? updatedItem : i);
        // Sort by order field to maintain proper ordering
        return updatedItems.sort((a, b) => (a.order || 0) - (b.order || 0));
      });
      setEditModalOpen(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating diary item:', error);
      // You might want to show an error message to the user here
      alert('Error al guardar los cambios. Por favor, inténtalo de nuevo.');
    }
  };
  // Cancel edit
  const handleCancelEdit = () => {
    setEditModalOpen(false);
    setEditingItem(null);
  };

  // Handle create plan
  const handleCreatePlan = async (newItem: Partial<DiaryItem>) => {
    if (!id) return;
    
    try {
      // Call the API to create the diary item
      const createdItem = await diaryService.createDiaryItem(id, newItem);
      
      // Update local state with the new item and sort by order
      setPlanItems(items => {
        const updatedItems = [...items, createdItem];
        // Sort by order field to maintain proper ordering
        return updatedItems.sort((a, b) => (a.order || 0) - (b.order || 0));
      });
      setCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating diary item:', error);
      alert('Error al crear el plan. Por favor, inténtalo de nuevo.');
    }
  };

  // Cancel create
  const handleCancelCreate = () => {
    setCreateModalOpen(false);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, item: DiaryItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (!draggedItem || !id) return;

    const dragIndex = filteredPlanItems.findIndex(item => item.id === draggedItem.id);
    if (dragIndex === dropIndex) return;

    // Create new array with reordered items
    const newItems = [...filteredPlanItems];
    const [removed] = newItems.splice(dragIndex, 1);
    newItems.splice(dropIndex, 0, removed);

    // Update order values based on new positions
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index + 1
    }));

    // Optimistically update the UI
    setPlanItems(prevItems => {
      const otherDayItems = prevItems.filter(item => 
        item.dayOfWeek !== selectedDayName && 
        item.dayOfWeek?.toLowerCase() !== selectedDayName.toLowerCase()
      );
      return [...otherDayItems, ...updatedItems].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    // Update all affected items in the backend
    try {
      const updatePromises = updatedItems.map(item => 
        diaryService.updateDiaryItem(id, item.id, item)
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating item order:', error);
      // Revert the optimistic update on error
      const revertedItems = await diaryService.getAllDiaryItemsByUserId(id);
      setPlanItems(revertedItems.sort((a, b) => (a.order || 0) - (b.order || 0)));
      alert('Error al reordenar los elementos. Por favor, inténtalo de nuevo.');
    }

    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };
  // For PatientDataForm compatibility
  const patientData: PatientData = patient ? {
    email: patient.email,
    username: patient.username,
    firstName: patient.firstName,
    lastName: patient.lastName,
    avatar: patient.avatarUrl,
    phoneNumber: patient.phone_number,
    gender: patient.gender as 'male' | 'female' | 'other',
    address: patient.address,
    dateOfBirth: patient.date_of_birth,
    rmrKcalDay: patient.rmrKcalDay,
    weight: patient.weight,
  } : fallbackPatientData;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setPatient((prev) => {
      if (!prev) return prev;
      
      // Map form field names to Patient interface field names
      const fieldMapping: Record<string, string> = {
        'phoneNumber': 'phone_number',
        'dateOfBirth': 'date_of_birth',
        'avatar': 'avatarUrl'
      };
      
      const patientFieldName = fieldMapping[name] || name;
      
      // Convert string values to numbers for numeric fields
      let processedValue: string | number = value;
      if (name === 'rmrKcalDay' || name === 'weight') {
        processedValue = value === '' ? 0 : parseInt(value, 10) || 0;
      }
      
      return { 
        ...prev, 
        [patientFieldName]: processedValue
      };
    });
  };
  const handleSave = () => {
    setEditMode(false);
    // Here you would call an API to save changes
  };

  if (loading) {
    return (
      <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '0' }}>
        <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app')} rightContent={<button style={{background:'none',border:'none',color:'#38b2ac',fontWeight:600,fontSize:16,cursor:'pointer'}}>Logout</button>} />
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#666' }}>Cargando datos del paciente...</div>
        </div>
      </div>
    );
  }


  if (!patient) {
    return (
      <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '0' }}>
        <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app')} rightContent={<button style={{background:'none',border:'none',color:'#38b2ac',fontWeight:600,fontSize:16,cursor:'pointer'}}>Logout</button>} />
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, color: '#666' }}>Paciente no encontrado.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '0' }}>
      <HeaderBar title="Detalle de paciente" onBack={() => navigate('/app')} rightContent={<button style={{background:'none',border:'none',color:'#38b2ac',fontWeight:600,fontSize:16,cursor:'pointer'}}>Logout</button>} />
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 40, marginBottom: 32 }}>
          {/* Left: Patient Info */}
          <Section style={{ minWidth: 320, maxWidth: 360, flex: '0 0 340px', marginBottom: 0 }}>
            <PatientDataForm
              patient={patientData}
              editMode={editMode}
              onChange={handleChange}
              onEditToggle={() => setEditMode((v) => !v)}
              onSave={e => { e.preventDefault(); handleSave(); }}
            />
          </Section>
          {/* Right: Plan, Tabs, Graphs */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Tabs tabs={["Estadísticas", "Plan", "Documentos"]} selected={selectedTab} onSelect={setSelectedTab} />
            {selectedTab === 0 && (
              <>
                <Section>
                  <div style={{ fontWeight: 700, marginBottom: 8, color: '#222' }}>Grafica Adherencia al plan</div>
                  <AdherenceChart 
                    data={adherenceData}
                    loading={adherenceLoading}
                    error={adherenceError}
                  />
                </Section>
                <Section style={{ marginBottom: 0 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8, color: '#222' }}>Grafica Energia, Sueño, Digestion</div>
                  <WellnessBarChart 
                    data={weeklyRecordsData}
                    loading={weeklyRecordsLoading}
                    error={weeklyRecordsError}
                  />
                </Section>
              </>
            )}
            {selectedTab === 1 && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 16 }}>
                  <button
                    onClick={() => setCreateModalOpen(true)}
                    style={{
                      background: '#38b2ac',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '8px 16px',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      marginLeft: 16
                    }}
                  >
                    + Crear Item
                  </button>
                  <WeekTabs selected={selectedDay} onSelect={setSelectedDay} />
                </div>
                {diaryLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    Cargando plan...
                  </div>
                ) : diaryError ? (
                  <div style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ color: '#e53e3e', marginBottom: 16 }}>{diaryError}</div>
                  </div>
                ) : filteredPlanItems.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    No hay actividades planificadas para {selectedDayName}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {filteredPlanItems.map((item, index) => (
                      <div 
                        key={`${item.id}-${item.dayOfWeek}-${index}`} 
                        style={{ 
                          position: 'relative',
                          opacity: draggedItem?.id === item.id ? 0.5 : 1,
                          transform: dragOverIndex === index ? 'translateY(-2px)' : 'none',
                          transition: 'all 0.2s ease',
                          borderTop: dragOverIndex === index ? '3px solid #38b2ac' : 'none',
                          paddingTop: dragOverIndex === index ? '8px' : '0'
                        }}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                      >
                        <div style={{ 
                          cursor: 'grab',
                          position: 'relative'
                        }}>
                          <DiaryItemCard item={item} />
                          {/* Drag handle indicator */}
                          <div style={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: '#999',
                            fontSize: 16,
                            cursor: 'grab',
                            userSelect: 'none'
                          }}>
                            ⋮⋮
                          </div>
                          <button
                            onClick={() => handleEditItem(item)}
                            style={{ position: 'absolute', top: 10, right: 10, background: '#38b2ac', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                          >Editar</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <DiaryItemEditModal
                  open={editModalOpen}
                  item={editingItem}
                  onChange={handleEditField}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                />
                <DiaryItemCreateModal
                  open={createModalOpen}
                  onSave={handleCreatePlan}
                  onCancel={handleCancelCreate}
                  defaultDayOfWeek={selectedDayName}
                />
              </Section>
            )}
            {selectedTab === 2 && (
              <Section style={{ flex: 1, marginBottom: 0 }}>
                <DocumentManager patientId={id || ''} />
              </Section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetail; 